
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChevronDown, ChevronUp } from "lucide-react";
import { ExternalRoundStats } from "@/hooks/useCourseStats";

interface HoleDetail {
  hole_number: number;
  green_in_regulation: boolean;
  scrambling?: boolean;
  sand_save?: boolean;
  bunker?: boolean;
}

const STATS_CUTOFF_DATE = '2025-12-18';
import { format } from "date-fns";
import { fr, enUS, es } from "date-fns/locale";

interface RoundsHistoryTableProps {
  stats: ExternalRoundStats[];
}

type SortField = "round_date" | "course_name" | "score_vs_par" | "fairway_percentage" | "gir_percentage" | "putts_per_hole";
type SortDirection = "asc" | "desc";

const RoundsHistoryTable = ({ stats }: RoundsHistoryTableProps) => {
  const { t, i18n } = useTranslation('course-stats');
  const [sortField, setSortField] = useState<SortField>("round_date");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const getLocale = () => {
    switch (i18n.language) {
      case 'fr': return fr;
      case 'es': return es;
      default: return enUS;
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const sortedStats = [...stats].sort((a, b) => {
    let aVal: any = a[sortField];
    let bVal: any = b[sortField];

    if (sortField === "round_date") {
      aVal = new Date(aVal).getTime();
      bVal = new Date(bVal).getTime();
    }

    if (aVal === null || aVal === undefined) aVal = sortDirection === "asc" ? Infinity : -Infinity;
    if (bVal === null || bVal === undefined) bVal = sortDirection === "asc" ? Infinity : -Infinity;

    if (sortDirection === "asc") {
      return aVal > bVal ? 1 : -1;
    }
    return aVal < bVal ? 1 : -1;
  });

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortDirection === "asc" ? (
      <ChevronUp className="h-4 w-4 inline ml-1" />
    ) : (
      <ChevronDown className="h-4 w-4 inline ml-1" />
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('history.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[480px]">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort("round_date")}
                  >
                    {t('history.date')} <SortIcon field="round_date" />
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort("course_name")}
                  >
                    {t('history.course')} <SortIcon field="course_name" />
                  </TableHead>
                  <TableHead className="text-center">{t('history.holes')}</TableHead>
                  <TableHead className="text-center">{t('history.score')}</TableHead>
                  <TableHead 
                    className="text-center cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort("score_vs_par")}
                  >
                    +/- {t('history.par')} <SortIcon field="score_vs_par" />
                  </TableHead>
                  <TableHead 
                    className="text-center cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort("fairway_percentage")}
                  >
                    {t('history.fairways')} <SortIcon field="fairway_percentage" />
                  </TableHead>
                  <TableHead 
                    className="text-center cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort("gir_percentage")}
                  >
                    {t('history.gir')} <SortIcon field="gir_percentage" />
                  </TableHead>
                  <TableHead 
                    className="text-center cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort("putts_per_hole")}
                  >
                    {t('history.putts')} <SortIcon field="putts_per_hole" />
                  </TableHead>
                  <TableHead className="text-center">
                    {t('history.scrambling')}
                  </TableHead>
                  <TableHead className="text-center">
                    {t('history.sandSave')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedStats.map((stat) => (
                  <TableRow key={stat.id}>
                    <TableCell>
                      {format(new Date(stat.round_date), "P", { locale: getLocale() })}
                    </TableCell>
                    <TableCell className="font-medium">
                      {stat.course_name || "-"}
                    </TableCell>
                    <TableCell className="text-center">{stat.holes_played}</TableCell>
                    <TableCell className="text-center font-semibold">
                      {stat.total_score || "-"}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className={
                        (stat.score_vs_par || 0) < 0 
                          ? "text-green-600 font-semibold" 
                          : (stat.score_vs_par || 0) > 0 
                            ? "text-red-600" 
                            : ""
                      }>
                        {stat.score_vs_par !== null 
                          ? (stat.score_vs_par >= 0 ? `+${stat.score_vs_par}` : stat.score_vs_par)
                          : "-"}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      {stat.fairway_percentage !== null 
                        ? `${stat.fairway_percentage.toFixed(0)}%` 
                        : "-"}
                    </TableCell>
                    <TableCell className="text-center">
                      {stat.gir_percentage !== null 
                        ? `${stat.gir_percentage.toFixed(0)}%` 
                        : "-"}
                    </TableCell>
                    <TableCell className="text-center">
                      {stat.putts_per_hole !== null 
                        ? stat.putts_per_hole.toFixed(2) 
                        : "-"}
                    </TableCell>
                    <TableCell className="text-center">
                      {(() => {
                        if (stat.round_date < STATS_CUTOFF_DATE) return "-";
                        const missedGIRs = (stat.holes_played ?? 0) - (stat.greens_in_regulation ?? 0);
                        const scramblingMade = stat.scrambling_made ?? 0;
                        const scramblingPct = missedGIRs > 0 ? (scramblingMade / missedGIRs) * 100 : null;
                        return scramblingPct !== null ? `${scramblingPct.toFixed(1)}%` : "-";
                      })()}
                    </TableCell>
                    <TableCell className="text-center">
                      {(() => {
                        if (stat.round_date < STATS_CUTOFF_DATE) return "-";
                        const holeDetails = stat.hole_details as HoleDetail[] | null;
                        if (!holeDetails || !Array.isArray(holeDetails)) return "-";
                        let opportunities = 0;
                        let successes = 0;
                        holeDetails.forEach((hole) => {
                          if (hole.bunker === true) {
                            opportunities++;
                            if (hole.sand_save === true) successes++;
                          }
                        });
                        return opportunities > 0 ? `${((successes / opportunities) * 100).toFixed(1)}%` : "-";
                      })()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </ScrollArea>
        
        {stats.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            {t('history.noData')}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RoundsHistoryTable;
